// Refatorado: componentes em src/components/Auth
import React, { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Navigate } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import AuthLeftPanel from "@/components/Auth/AuthLeftPanel";
import { AuthTabs } from "@/components/Auth/AuthTabs";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

/**
 * Determina a rota esperada baseado no estado do onboarding
 */
function getExpectedRoute(step: string | null, clientId: string | null, role: string | null): string {
  switch (step) {
    case "completed":
      if (role === 'admin' || role === 'technician') {
        return "/dashboard";
      }
      return "/portal";
    case "plan":
    case "company":
      if (clientId) {
        return "/onboarding/plan";
      }
      return "/onboarding/company";
    case "personal":
      return "/onboarding/company";
    case "verified":
    case "email_sent":
    case "pending":
    default:
      return "/onboarding/personal";
  }
}

const Auth = () => {
  const { signIn, signUp, user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);


  // Verificar onboarding status quando usuário está autenticado
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user || loading) return;

      setCheckingOnboarding(true);
      
      try {
        // Verificar se é admin redirect
        const originalPath = sessionStorage.getItem('admin-redirect');
        if (originalPath === '/admin') {
          sessionStorage.removeItem('admin-redirect');
          setRedirectPath('/admin');
          return;
        }

        // Buscar status do onboarding
        const { data: profile } = await supabase
          .from("profiles")
          .select("onboarding_step, client_id, role")
          .eq("id", user.id)
          .single();

        let onboardingStep = profile?.onboarding_step || "pending";
        let clientId = profile?.client_id || null;
        let role = profile?.role || "user";

        

        // Se não tem cliente, tentar associar por domínio automaticamente
        if (!clientId && user.email) {
          const domain = user.email.split('@')[1];
          
          // Buscar cliente com esse domínio
          const { data: clientData } = await supabase
            .from("clients")
            .select("id")
            .eq("dominio", domain)
            .eq("status", "active")
            .single();

          if (clientData?.id) {
            
            
            // Atualizar profile com client_id, role user e marcar onboarding como completo
            await supabase
              .from("profiles")
              .update({ 
                client_id: clientData.id, 
                onboarding_step: "completed",
                role: "user",  // Usuários associados por domínio recebem role user
                updated_at: new Date().toISOString()
              })
              .eq("id", user.id);

            // Atualizar users também com role user
            await supabase
              .from("users")
              .update({ 
                client_id: clientData.id,
                role: "user",  // Usuários associados por domínio recebem role user
                updated_at: new Date().toISOString()
              })
              .eq("id", user.id);

            clientId = clientData.id;
            onboardingStep = "completed";
            
            toast({
              title: "Bem-vindo!",
              description: "Você foi associado automaticamente à sua empresa.",
            });
          }
        }

        

        // Determinar rota baseada no onboarding
        const expectedRoute = getExpectedRoute(onboardingStep, clientId, role);
        setRedirectPath(expectedRoute);
      } catch (error) {
        console.error("Auth - Erro ao verificar onboarding:", error);
        setRedirectPath("/onboarding/personal");
      } finally {
        setCheckingOnboarding(false);
      }
    };

    checkOnboardingStatus();
  }, [user, loading, toast]);

  // Redirecionar quando tiver o path determinado
  if (redirectPath && user && !loading && !checkingOnboarding) {
    
    return <Navigate to={redirectPath} replace />;
  }

  // Mostrar loading enquanto verifica onboarding
  if (user && (loading || checkingOnboarding)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Verificando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row">
      <AuthLeftPanel />
      <div 
        className="flex-1 flex items-center justify-center bg-gradient-to-tl from-amber-50 via-orange-50 to-white min-h-screen lg:min-h-0 p-4 sm:p-6 lg:p-8 overflow-y-auto"
        style={{
          backgroundImage: 'url(/logos/images/fundo_apresentacao_eleeva.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <AuthTabs loading={loading} signIn={signIn} signUp={signUp} />
      </div>
    </div>
  );
};

export default Auth;
